// bootstrap/app.js
import Vue from 'vue'
import App from '@/App.vue'
import '@styles/style.scss'
import '@app/RegisterGlobalComponents'
import '@app/Directives'
import '@app/Filters'
import { setupPackages } from './packages'
import { initSentry } from './sentry'
import Logger from '@/packages/logger'
import apolloClient from '@/packages/apollo-client'

Vue.config.productionTip = false

export async function createApp () {
	const { i18n, router, store, apolloProvider, Permissions } = await setupPackages()

	// Register plugins
	Vue.use(Permissions)

	// Initialize Sentry
	initSentry(Vue, router)

	// Global error handler
	Vue.config.errorHandler = err => Logger.captureException(err)

	// Create Vue instance
	const app = new Vue({
		router: router.VueRouter,
		i18n,
		store,
		apolloProvider,
		render: h => h(App)
	}).$mount('#app')

	// Periodic cache GC to prevent cache bloat, decoupled from navigation.
	// Runs only when the browser is idle and no route transition is in progress,
	// since gc() walks the whole normalized cache and can block the main thread.
	let isNavigating = false
	let navigationStartedAt = null
	const NAVIGATION_TIMEOUT_MS = 10000

	const beginNavigation = () => {
		isNavigating = true
		navigationStartedAt = Date.now()
	}

	const endNavigation = () => {
		isNavigating = false
		navigationStartedAt = null
	}

	const isNavigationOngoing = () => {
		if (!isNavigating) return false
		if (!navigationStartedAt) return false
		if (Date.now() - navigationStartedAt > NAVIGATION_TIMEOUT_MS) {
			endNavigation()
			return false
		}
		return true
	}

	router.beforeEach(({ next }) => {
		beginNavigation()
		next()
	})
	router.VueRouter.afterEach(() => {
		endNavigation()
	})
	if (router.VueRouter && typeof router.VueRouter.onError === 'function') {
		router.VueRouter.onError(() => {
			endNavigation()
		})
	}

	const runCacheGc = () => {
		if (isNavigationOngoing()) return
		if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(() => {
				if (!isNavigationOngoing()) {
					apolloClient.cache.gc()
				}
			}, { timeout: 5000 })
		} else {
			apolloClient.cache.gc()
		}
	}
	setInterval(runCacheGc, 30000)

	return { app, router, store }
}
